import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Chip,
  LinearProgress,
  Rating,
  Paper,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Build,
  CheckCircle,
  Cancel,
  Pause,
} from '@mui/icons-material';
import { Partner } from '@/services/partnerService';

interface PartnerDetailDialogProps {
  open: boolean;
  onClose: () => void;
  partner: Partner | null;
  onEdit?: () => void;
}

export const PartnerDetailDialog: React.FC<PartnerDetailDialogProps> = ({
  open,
  onClose,
  partner,
  onEdit,
}) => {
  if (!partner) return null;

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'inactive': return <Pause />;
      case 'suspended': return <Cancel />;
      default: return <CheckCircle />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{partner.name}</Typography>
          <Chip
            icon={getStatusIcon(partner.status)}
            label={partner.status || 'active'}
            color={getStatusColor(partner.status) as any}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Basic Information
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Build fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Service Type:</strong> {partner.serviceType}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email fontSize="small" color="action" />
                    <Typography variant="body2">
                      {partner.email || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone fontSize="small" color="action" />
                    <Typography variant="body2">
                      {partner.phone || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  {(partner.address || partner.city || partner.state) && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Box>
                        {partner.address && (
                          <Typography variant="body2">{partner.address}</Typography>
                        )}
                        {(partner.city || partner.state) && (
                          <Typography variant="body2">
                            {partner.city}{partner.city && partner.state ? ', ' : ''}{partner.state}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Performance Metrics */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Performance Metrics
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                  {partner.trustRating.overall}
                </Typography>
                <Rating value={partner.trustRating.overall} readOnly size="large" />
                <Typography variant="caption" color="text.secondary" display="block">
                  Overall Trust Rating
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Accuracy</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {partner.trustRating.accuracy}/5
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(partner.trustRating.accuracy / 5) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Timeliness</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {partner.trustRating.timeliness}/5
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(partner.trustRating.timeliness / 5) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                      color="success"
                    />
                  </Box>

                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Documentation</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {partner.trustRating.documentation}/5
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(partner.trustRating.documentation / 5) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                      color="info"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Job Statistics */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Job Statistics
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: 'grey.50', textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {partner.completedJobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {partner.serviceType.toLowerCase()}s completed
              </Typography>
            </Paper>
          </Grid>

          {/* Timestamps */}
          {(partner.createdAt || partner.updatedAt) && (
            <Grid item xs={12}>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                {partner.createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(partner.createdAt).toLocaleDateString()}
                  </Typography>
                )}
                {partner.updatedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Updated: {new Date(partner.updatedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onEdit && (
          <Button variant="contained" onClick={onEdit}>
            Edit Partner
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
